import React from 'react';
import { Car, Shield, Gauge, Users, Wrench, CheckCircle } from 'lucide-react';

const Features = () => {
  const specifications = [
    {
      icon: Car,
      title: '3.5-Ton Renault Master',
      description: 'Modern, reliable vehicle that\'s easy to drive with a standard license',
      details: ['No special license required', 'Fuel efficient', 'Smooth handling']
    },
    {
      icon: Shield,
      title: 'Built by Foxy Horseboxes',
      description: 'Crafted to the highest standards with premium materials and safety features',
      details: ['2025 build', 'Premium finish', 'Industry-leading quality']
    },
    {
      icon: Gauge,
      title: 'Comfortable Journey',
      description: 'Spacious interior designed for your horse\'s comfort and safety',
      details: ['DEFRA approved', 'Smooth suspension', 'Blue calming lights for stress-free travel']
    },
    {
      icon: Users,
      title: 'Easy to Use',
      description: 'User-friendly design with all features clearly accessible',
      details: ['Simple controls', 'Clear instructions', 'Support available']
    },
    {
      icon: Wrench,
      title: 'Well Maintained',
      description: 'Regular servicing and thorough checks before every hire',
      details: ['Professional maintenance', 'Cleaned and disinfected after every hire', 'Safety inspected']
    },
    {
      icon: CheckCircle,
      title: 'Fully Insured',
      description: 'Comprehensive insurance coverage for your peace of mind',
      details: ['Full coverage', 'Roadside assistance and Equine Rescue', 'Transparent terms']
    }
  ];

  return (
    <section id="features" className="py-20 lg:py-28 bg-white">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 font-['Playfair_Display']">
            Our <span className="text-[#2d5f3f]">Horsebox Features</span>
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Experience the difference with our state-of-the-art horsebox, designed with both 
            horse and driver comfort in mind.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {specifications.map((spec, index) => {
            const Icon = spec.icon;
            return (
              <div
                key={index}
                className="group relative bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 border border-gray-200 hover:border-[#2d5f3f]/30 transition-all duration-300 hover:shadow-xl hover:-translate-y-2"
              >
                {/* Icon */}
                <div className="relative mb-6">
                  <div className="absolute inset-0 bg-[#2d5f3f]/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative w-16 h-16 rounded-2xl bg-[#2d5f3f]/10 flex items-center justify-center group-hover:bg-[#2d5f3f] group-hover:scale-110 transition-all duration-300">
                    <Icon className="w-8 h-8 text-[#2d5f3f] group-hover:text-white transition-colors duration-300" />
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {spec.title}
                </h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  {spec.description}
                </p>

                {/* Details List */}
                <ul className="space-y-2">
                  {spec.details.map((detail, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#2d5f3f] mr-2"></div>
                      {detail}
                    </li>
                  ))}
                </ul>

                {/* Hover effect overlay */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#2d5f3f]/0 to-[#2d5f3f]/0 group-hover:from-[#2d5f3f]/5 group-hover:to-transparent transition-all duration-300 pointer-events-none"></div>
              </div>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <div className="inline-block bg-gradient-to-r from-[#f5f3ed] to-white rounded-2xl p-8 border border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              Want to Learn More About Our Horsebox?
            </h3>
            <p className="text-gray-600 mb-6 max-w-2xl">
              View detailed specifications and features, or contact us for any questions you may have.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/horsebox-info"
                className="px-8 py-3 bg-[#2d5f3f] text-white rounded-lg hover:bg-[#1e4029] transition-colors duration-300 font-semibold"
              >
                View Full Specifications
              </a>
              <a
                href="tel:07788754165"
                className="px-8 py-3 bg-white text-[#2d5f3f] border-2 border-[#2d5f3f] rounded-lg hover:bg-[#2d5f3f] hover:text-white transition-colors duration-300 font-semibold"
              >
                Call Us Now
              </a>
              <a
                href="mailto:enquiries@equi-go.co.uk"
                className="px-8 py-3 bg-white text-[#2d5f3f] border-2 border-[#2d5f3f] rounded-lg hover:bg-[#2d5f3f] hover:text-white transition-colors duration-300 font-semibold"
              >
                Email Enquiry
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;